import React, { useState, useEffect, useRef, useCallback } from 'react';
import Badge from '@mui/material/Badge';
import { IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useCalendario } from '../contexts/CalendarioContext';
import { useAuth } from '../auth/AuthContext';

// Import components for tabs
import WhatsAppPanelDrawer from './WhatsAppPanelDrawer';
import FeedbackToolPanel from './FeedbackToolPanel';
import ChatIA from './ChatIA';
import CalendarToolContent from './CalendarToolContent';
// Note: We'll need a "CalendarOnly" version or just pass a prop to CalendarioLateral 
// to render only the calendar content. For now, we'll implement the calendar part here
// or import it if we refactor. To keep it simple and safe, I'll reuse components.

import '../style/components/AdminRightSidebar.css';

const SIDEBAR_WIDTH_KEY = 'admin_sidebar_width';
const ACTIVE_TAB_KEY = 'admin_sidebar_active_tab';

const AdminRightSidebar = () => {
    const { user } = useAuth();
    const { calendarioAbierto, setCalendarioAbierto } = useCalendario();
    
    // State
    const [width, setWidth] = useState(() => {
        const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
        return saved ? parseInt(saved, 10) : 350;
    });
    const [activeTab, setActiveTab] = useState(() => {
        return localStorage.getItem(ACTIVE_TAB_KEY) || 'whatsapp';
    });
    const [isResizing, setIsResizing] = useState(false);
    const [chatsConNoLeidos, setChatsConNoLeidos] = useState(0);
    // Etapa intermedia: al expandir desde oculto, primero se ve el menú con las 4
    // opciones (Mensajes/Calendario/Chatbot/Feedback) y recién al elegir una se abre
    // su contenido. false = mostrar menú, true = mostrar contenido de activeTab.
    const [tabElegido, setTabElegido] = useState(false);

    const sidebarRef = useRef(null);

    // Retrocede un paso por vez: contenido → menú → oculto. Desde oculto, abre el menú.
    const toggleSidebar = () => {
        if (!calendarioAbierto) {
            setCalendarioAbierto(true);
            setTabElegido(false);
        } else if (tabElegido) {
            setTabElegido(false);
        } else {
            setCalendarioAbierto(false);
        }
    };

    // Save state to localStorage
    useEffect(() => {
        localStorage.setItem(SIDEBAR_WIDTH_KEY, width.toString());
    }, [width]);

    useEffect(() => {
        localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
    }, [activeTab]);

    useEffect(() => {
        document.documentElement.style.setProperty('--admin-right-sidebar-width', calendarioAbierto ? `${width}px` : '0px');

        return () => {
            document.documentElement.style.setProperty('--admin-right-sidebar-width', '0px');
        };
    }, [calendarioAbierto, width]);

    useEffect(() => {
        const handleOpenSidebar = (e) => {
            if (e.detail?.tab) {
                setActiveTab(e.detail.tab);
                setTabElegido(true);
            }
            setCalendarioAbierto(true);
        };
        window.addEventListener('openAdminSidebar', handleOpenSidebar);
        return () => window.removeEventListener('openAdminSidebar', handleOpenSidebar);
    }, [setCalendarioAbierto]);

    // Resize Logic
    const startResizing = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    const stopResizing = useCallback(() => {
        setIsResizing(false);
    }, []);

    const resize = useCallback((e) => {
        if (isResizing) {
            const newWidth = window.innerWidth - e.clientX;
            if (newWidth >= 280 && newWidth <= 600) {
                setWidth(newWidth);
            }
        }
    }, [isResizing]);

    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', resize);
            window.addEventListener('mouseup', stopResizing);
        } else {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        }
        return () => {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, [isResizing, resize, stopResizing]);


    const iconoWhatsAppConBadge = (
        <Badge
            badgeContent={chatsConNoLeidos > 99 ? '99+' : chatsConNoLeidos}
            color="success"
            invisible={chatsConNoLeidos <= 0}
            sx={{
                '& .MuiBadge-badge': {
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    minWidth: 18,
                    height: 18,
                },
            }}
        >
            <WhatsAppIcon />
        </Badge>
    );

    const tabs = [
        {
            id: 'whatsapp',
            icon: iconoWhatsAppConBadge,
            label: 'Mensajes',
            component: <WhatsAppPanelDrawer onUnreadChatsChange={setChatsConNoLeidos} />
        },
        { id: 'calendario', icon: <CalendarMonthIcon />, label: 'Calendario', component: <CalendarToolContent /> },
        { id: 'chatbot', icon: <SmartToyIcon />, label: 'Chatbot', component: <ChatIA usuarioId={user?.id || 1} rol={user?.rol || 'ADMIN'} /> },
        { id: 'feedback', icon: <FeedbackIcon />, label: 'Feedback', component: <FeedbackToolPanel /> }
    ];

    const elegirTab = (tabId) => {
        setActiveTab(tabId);
        setTabElegido(true);
    };

    // La X del panel de contenido vuelve al menú (etapa 2), no cierra todo.
    const volverAMenu = () => setTabElegido(false);

    return (
        <>
            {/* Botón flotante: comprime el panel (queda oculto) / lo expande de nuevo */}
            <button
                className={`sidebar-toggle-btn lengueta ${calendarioAbierto ? 'open' : ''} ${tabElegido ? 'expandido' : ''} ${isResizing ? 'resizing' : ''}`}
                onClick={toggleSidebar}
                style={{ right: !calendarioAbierto ? '0' : (tabElegido ? `${width}px` : '96px') }}
                title={calendarioAbierto ? 'Comprimir panel' : 'Expandir panel'}
            >
                {calendarioAbierto ? (
                    <>
                        <CloseIcon className="sidebar-toggle-flecha" />
                        <span className="sidebar-toggle-etiqueta">CERRAR</span>
                    </>
                ) : (
                    <>
                        <ChevronLeftIcon className="sidebar-toggle-flecha" />
                        <span className="sidebar-toggle-etiqueta">HERRAMIENTAS</span>
                    </>
                )}
            </button>

            {/* Etapa 2: menú con las 4 opciones, sin contenido todavía */}
            {calendarioAbierto && !tabElegido && (
                <aside className="admin-right-rail">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            className="admin-right-rail-icon"
                            onClick={() => elegirTab(tab.id)}
                            title={tab.label}
                        >
                            {tab.icon}
                            <span className="admin-right-rail-label">{tab.label}</span>
                        </button>
                    ))}
                </aside>
            )}

            {/* Etapa 3: contenido de la opción elegida */}
            {calendarioAbierto && tabElegido && (
                <aside
                    ref={sidebarRef}
                    className="admin-right-sidebar"
                    style={{ width: `${width}px` }}
                >
                    {/* Resize Handle */}
                    <div
                        className={`sidebar-resize-handle ${isResizing ? 'resizing' : ''}`}
                        onMouseDown={startResizing}
                    />

                    <div className="sidebar-header">
                        <div className="sidebar-title-row">
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: width < 300 ? '1rem' : '1.25rem' }}>
                                {tabs.find(t => t.id === activeTab)?.label}
                            </Typography>
                            {/* Dos salidas distintas, cada una para su tamaño de pantalla.
                                En escritorio la X vuelve al menú de herramientas, que queda
                                al costado. En mobile el panel ocupa toda la pantalla y la
                                lengüeta de afuera queda tapada, así que ahí la X cierra todo:
                                es la única salida a mano. No se pierde nada, porque las otras
                                herramientas se eligen desde las pestañas de acá abajo. */}
                            <IconButton size="small" onClick={volverAMenu}
                                        className="close-sidebar-btn admin-sidebar-solo-escritorio"
                                        title="Volver al menú">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={toggleSidebar}
                                        className="close-sidebar-btn admin-sidebar-solo-mobile"
                                        title="Cerrar el panel">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>

                        <div className="sidebar-tabs">
                            {/* Solo se listan las pestañas que NO están en uso — la activa ya
                                se identifica por el título de arriba, no hace falta repetirla. */}
                            {tabs.filter(tab => tab.id !== activeTab).map((tab) => (
                                <button
                                    key={tab.id}
                                    className="sidebar-tab"
                                    onClick={() => setActiveTab(tab.id)}
                                    style={{ flex: '1' }}
                                >
                                    {tab.icon}
                                    {width >= 320 && <span>{tab.label}</span>}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="sidebar-content">
                        {tabs.find(t => t.id === activeTab)?.component}
                    </div>
                </aside>
            )}
        </>
    );
};

export default AdminRightSidebar;

